/**
 * @fileoverview Icons Section - Component Library
 *
 * Purpose: Display available icons from lucide-react library
 * Used by: Component library main page
 */

import { ComponentShowcase } from "./-ui.component-showcase";
import { Input } from "@/ui/primitives/input";
import { Badge } from "@/ui/primitives/badge";
import {
  // UI Icons
  Home,
  Settings,
  Search,
  Filter,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  Check,
  AlertCircle,
  Info,
  HelpCircle,
  Loader2,
  // Navigation
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  // Communication
  Mail,
  Send,
  MessageSquare,
  Phone,
  Video,
  Bell,
  Inbox,
  // Files & Folders
  File,
  Folder,
  FileText,
  Download,
  Upload,
  Paperclip,
  Image,
  FileCode,
  // User & Account
  User,
  Users,
  UserPlus,
  LogOut,
  LogIn,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  // Editing
  Edit,
  Trash2,
  Copy,
  Clipboard,
  Save,
  Share2,
  Star,
  Heart,
  // Media
  Play,
  Pause,
  Volume2,
  VolumeX,
  Camera,
  Mic,
  MicOff,
  // Shopping & Commerce
  ShoppingCart,
  CreditCard,
  DollarSign,
  Tag,
  Package,
  // Calendar & Time
  Calendar,
  Clock,
  Timer,
  // Social
  Share,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  AtSign,
  // Status
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Zap,
  TrendingUp,
  TrendingDown,
  // Tools
  Wrench,
  Hammer,
  Cog,
  Sliders,
  Maximize2,
  Minimize2,
  // Data
  BarChart,
  LineChart,
  PieChart,
  Activity,
  Database,
  Server,
  // Weather
  Sun,
  Moon,
  Cloud,
  CloudRain,
  // Misc
  Gift,
  Award,
  Target,
  Flag,
  Bookmark,
  MapPin,
  Globe,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useState } from "react";

const iconCategories = [
  {
    name: "UI & Navigation",
    icons: [
      { Icon: Home, name: "Home" },
      { Icon: Settings, name: "Settings" },
      { Icon: Search, name: "Search" },
      { Icon: Filter, name: "Filter" },
      { Icon: Menu, name: "Menu" },
      { Icon: X, name: "X" },
      { Icon: ChevronDown, name: "ChevronDown" },
      { Icon: ChevronUp, name: "ChevronUp" },
      { Icon: ChevronLeft, name: "ChevronLeft" },
      { Icon: ChevronRight, name: "ChevronRight" },
      { Icon: Plus, name: "Plus" },
      { Icon: Minus, name: "Minus" },
      { Icon: ArrowLeft, name: "ArrowLeft" },
      { Icon: ArrowRight, name: "ArrowRight" },
      { Icon: ArrowUp, name: "ArrowUp" },
      { Icon: ArrowDown, name: "ArrowDown" },
      { Icon: ExternalLink, name: "ExternalLink" },
    ],
  },
  {
    name: "Status & Feedback",
    icons: [
      { Icon: Check, name: "Check" },
      { Icon: CheckCircle2, name: "CheckCircle2" },
      { Icon: XCircle, name: "XCircle" },
      { Icon: AlertCircle, name: "AlertCircle" },
      { Icon: AlertTriangle, name: "AlertTriangle" },
      { Icon: Info, name: "Info" },
      { Icon: HelpCircle, name: "HelpCircle" },
      { Icon: Loader2, name: "Loader2" },
      { Icon: Zap, name: "Zap" },
      { Icon: TrendingUp, name: "TrendingUp" },
      { Icon: TrendingDown, name: "TrendingDown" },
    ],
  },
  {
    name: "Communication",
    icons: [
      { Icon: Mail, name: "Mail" },
      { Icon: Send, name: "Send" },
      { Icon: MessageSquare, name: "MessageSquare" },
      { Icon: MessageCircle, name: "MessageCircle" },
      { Icon: Phone, name: "Phone" },
      { Icon: Video, name: "Video" },
      { Icon: Bell, name: "Bell" },
      { Icon: Inbox, name: "Inbox" },
      { Icon: AtSign, name: "AtSign" },
    ],
  },
  {
    name: "Files & Documents",
    icons: [
      { Icon: File, name: "File" },
      { Icon: Folder, name: "Folder" },
      { Icon: FileText, name: "FileText" },
      { Icon: FileCode, name: "FileCode" },
      { Icon: Download, name: "Download" },
      { Icon: Upload, name: "Upload" },
      { Icon: Paperclip, name: "Paperclip" },
      { Icon: Image, name: "Image" },
    ],
  },
  {
    name: "User & Account",
    icons: [
      { Icon: User, name: "User" },
      { Icon: Users, name: "Users" },
      { Icon: UserPlus, name: "UserPlus" },
      { Icon: LogOut, name: "LogOut" },
      { Icon: LogIn, name: "LogIn" },
      { Icon: Lock, name: "Lock" },
      { Icon: Unlock, name: "Unlock" },
      { Icon: Eye, name: "Eye" },
      { Icon: EyeOff, name: "EyeOff" },
    ],
  },
  {
    name: "Editing & Actions",
    icons: [
      { Icon: Edit, name: "Edit" },
      { Icon: Trash2, name: "Trash2" },
      { Icon: Copy, name: "Copy" },
      { Icon: Clipboard, name: "Clipboard" },
      { Icon: Save, name: "Save" },
      { Icon: Share2, name: "Share2" },
      { Icon: Share, name: "Share" },
      { Icon: Star, name: "Star" },
      { Icon: Heart, name: "Heart" },
    ],
  },
  {
    name: "Media & Playback",
    icons: [
      { Icon: Play, name: "Play" },
      { Icon: Pause, name: "Pause" },
      { Icon: Volume2, name: "Volume2" },
      { Icon: VolumeX, name: "VolumeX" },
      { Icon: Camera, name: "Camera" },
      { Icon: Mic, name: "Mic" },
      { Icon: MicOff, name: "MicOff" },
    ],
  },
  {
    name: "Commerce",
    icons: [
      { Icon: ShoppingCart, name: "ShoppingCart" },
      { Icon: CreditCard, name: "CreditCard" },
      { Icon: DollarSign, name: "DollarSign" },
      { Icon: Tag, name: "Tag" },
      { Icon: Package, name: "Package" },
      { Icon: Gift, name: "Gift" },
    ],
  },
  {
    name: "Calendar & Time",
    icons: [
      { Icon: Calendar, name: "Calendar" },
      { Icon: Clock, name: "Clock" },
      { Icon: Timer, name: "Timer" },
    ],
  },
  {
    name: "Data & Charts",
    icons: [
      { Icon: BarChart, name: "BarChart" },
      { Icon: LineChart, name: "LineChart" },
      { Icon: PieChart, name: "PieChart" },
      { Icon: Activity, name: "Activity" },
      { Icon: Database, name: "Database" },
      { Icon: Server, name: "Server" },
    ],
  },
  {
    name: "Tools & Settings",
    icons: [
      { Icon: Wrench, name: "Wrench" },
      { Icon: Hammer, name: "Hammer" },
      { Icon: Cog, name: "Cog" },
      { Icon: Sliders, name: "Sliders" },
      { Icon: Maximize2, name: "Maximize2" },
      { Icon: Minimize2, name: "Minimize2" },
    ],
  },
  {
    name: "Social & Engagement",
    icons: [
      { Icon: ThumbsUp, name: "ThumbsUp" },
      { Icon: ThumbsDown, name: "ThumbsDown" },
    ],
  },
  {
    name: "Location & Weather",
    icons: [
      { Icon: MapPin, name: "MapPin" },
      { Icon: Globe, name: "Globe" },
      { Icon: Sun, name: "Sun" },
      { Icon: Moon, name: "Moon" },
      { Icon: Cloud, name: "Cloud" },
      { Icon: CloudRain, name: "CloudRain" },
    ],
  },
  {
    name: "Miscellaneous",
    icons: [
      { Icon: Award, name: "Award" },
      { Icon: Target, name: "Target" },
      { Icon: Flag, name: "Flag" },
      { Icon: Bookmark, name: "Bookmark" },
      { Icon: Wifi, name: "Wifi" },
      { Icon: WifiOff, name: "WifiOff" },
    ],
  },
];

export function IconsSection() {
  const [searchQuery, setSearchQuery] = useState("");

  // Flatten all icons for searching
  const allIcons = iconCategories.flatMap((category) =>
    category.icons.map((icon) => ({ ...icon, category: category.name })),
  );

  // Filter icons based on search
  const filteredIcons = searchQuery
    ? allIcons.filter((icon) =>
        icon.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : null;

  return (
    <div id="icons" className="scroll-mt-24 space-y-12">
      <div>
        <h1 className="mb-2 text-4xl font-bold tracking-tight">Icons</h1>
        <p className="text-lg text-muted-foreground">
          Icon library from{" "}
          <a
            href="https://lucide.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Lucide Icons
          </a>
          . Browse available icons and copy the import statements.
        </p>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <Input
          type="search"
          placeholder="Search icons... (e.g., 'user', 'mail', 'check')"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
        />
        {searchQuery && (
          <p className="mt-2 text-sm text-muted-foreground">
            Found {filteredIcons?.length || 0} icon
            {filteredIcons?.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      {/* Search Results */}
      {filteredIcons && filteredIcons.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-2xl font-bold tracking-tight">Search Results</h2>
          <div className="rounded-lg border bg-card p-8">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {filteredIcons.map(({ Icon, name, category }) => (
                <button
                  key={name}
                  onClick={() => {
                    navigator.clipboard.writeText(name);
                  }}
                  className="group flex flex-col items-center gap-2 rounded-lg p-4 transition-colors hover:bg-accent"
                  title={`Click to copy: ${name}`}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-center text-xs font-medium">
                    {name}
                  </span>
                  <Badge variant="outline" className="px-1 py-0 text-[10px]">
                    {category}
                  </Badge>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* No Results */}
      {filteredIcons && filteredIcons.length === 0 && (
        <div className="rounded-lg border bg-card p-12 text-center">
          <p className="text-muted-foreground">
            No icons found for "{searchQuery}". Try searching for common terms
            like "user", "mail", or "check".
          </p>
        </div>
      )}

      {/* Icon Categories */}
      {!searchQuery &&
        iconCategories.map((category) => (
          <section key={category.name} className="space-y-4">
            <div>
              <h2 className="mb-1 text-2xl font-bold tracking-tight">
                {category.name}
              </h2>
              <p className="text-sm text-muted-foreground">
                {category.icons.length} icon
                {category.icons.length !== 1 ? "s" : ""} in this category
              </p>
            </div>
            <div className="rounded-lg border bg-card p-8">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {category.icons.map(({ Icon, name }) => (
                  <button
                    key={name}
                    onClick={() => {
                      navigator.clipboard.writeText(name);
                    }}
                    className="group relative flex flex-col items-center gap-2 rounded-lg p-4 transition-colors hover:bg-accent"
                    title={`Click to copy: ${name}`}
                  >
                    <Icon className="h-6 w-6" />
                    <span className="break-all text-center text-xs font-medium">
                      {name}
                    </span>
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-accent/90 opacity-0 transition-opacity group-hover:opacity-100">
                      <span className="text-xs font-semibold">Copy</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        ))}

      {/* Icon Sizes */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">Icon Sizes</h2>
          <p className="text-muted-foreground">
            Common icon size variants used throughout the design system.
          </p>
        </div>
        <ComponentShowcase
          title="Size Examples"
          description="Icons at different sizes"
        >
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {[
              {
                size: "h-3 w-3",
                label: "Extra Small (12px)",
                desc: "Inline text icons",
              },
              {
                size: "h-4 w-4",
                label: "Small (16px)",
                desc: "Button icons, badges",
              },
              { size: "h-5 w-5", label: "Medium (20px)", desc: "Default size" },
              {
                size: "h-6 w-6",
                label: "Large (24px)",
                desc: "Headers, emphasis",
              },
              {
                size: "h-8 w-8",
                label: "Extra Large (32px)",
                desc: "Feature icons",
              },
              { size: "h-12 w-12", label: "2XL (48px)", desc: "Hero icons" },
              { size: "h-16 w-16", label: "3XL (64px)", desc: "Empty states" },
              {
                size: "h-24 w-24",
                label: "4XL (96px)",
                desc: "Large displays",
              },
            ].map((size) => (
              <div
                key={size.size}
                className="flex flex-col items-center gap-3 rounded-lg border bg-muted/50 p-4"
              >
                <Search className={size.size} />
                <div className="text-center">
                  <div className="text-sm font-medium">{size.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {size.desc}
                  </div>
                  <code className="mt-1 block font-mono text-[10px] text-muted-foreground">
                    className="{size.size}"
                  </code>
                </div>
              </div>
            ))}
          </div>
        </ComponentShowcase>
      </section>

      {/* How to Use */}
      <section className="space-y-6 rounded-lg border bg-card p-8 text-card-foreground">
        <h2 className="text-xl font-semibold">How to Use Icons</h2>

        <div className="space-y-4 text-sm">
          <div>
            <p className="mb-2 font-medium text-foreground">
              1. Import the icon
            </p>
            <div className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
              <pre>{`import { Search, User, Settings } from "lucide-react";`}</pre>
            </div>
          </div>

          <div>
            <p className="mb-2 font-medium text-foreground">
              2. Use in your component
            </p>
            <div className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
              <pre>{`// As standalone
<Search className="h-4 w-4" />

// In button
<Button>
  <Search className="mr-2 h-4 w-4" />
  Search
</Button>

// With custom color
<User className="h-5 w-5 text-primary" />`}</pre>
            </div>
          </div>

          <div>
            <p className="mb-2 font-medium text-foreground">
              3. Common size classes
            </p>
            <ul className="ml-2 list-inside list-disc space-y-1 text-muted-foreground">
              <li>
                <code className="rounded bg-muted px-1.5 py-0.5 text-foreground">
                  h-4 w-4
                </code>{" "}
                - Standard button icons (16px)
              </li>
              <li>
                <code className="rounded bg-muted px-1.5 py-0.5 text-foreground">
                  h-5 w-5
                </code>{" "}
                - Default standalone icons (20px)
              </li>
              <li>
                <code className="rounded bg-muted px-1.5 py-0.5 text-foreground">
                  h-6 w-6
                </code>{" "}
                - Headers and emphasis (24px)
              </li>
              <li>
                <code className="rounded bg-muted px-1.5 py-0.5 text-foreground">
                  h-8 w-8
                </code>{" "}
                - Large feature icons (32px)
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-2 font-medium text-foreground">
              4. Click to copy icon name
            </p>
            <p className="text-muted-foreground">
              Click any icon above to copy its name to clipboard. You can then
              paste it into your import statement.
            </p>
          </div>

          <div className="border-t pt-4">
            <p className="mb-2 font-medium text-foreground">Browse all icons</p>
            <p className="mb-3 text-muted-foreground">
              Lucide has 1000+ icons available. Visit{" "}
              <a
                href="https://lucide.dev/icons"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                lucide.dev/icons
              </a>{" "}
              to browse the complete library.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
